// Saved-canvas storage. A "canvas template" is a named bundle of
// kernel references (by command) that re-renders live each time it's
// opened — same role as a saved kernel, but for the whole canvas as
// a multi-widget view.
//
// Shape:
//   {
//     id: string,                 // stable uuid-ish
//     slug: string,               // kebab-case, used in admin URLs + uniqueness
//     title: string,              // human label
//     description?: string,
//     rows: [{                    // rows of cells; cells in a row render side by side
//       cells: [{
//         command: string,        // kernel command (must exist in kernelStore at view time)
//         caption?: string,       // optional caption shown above the widget
//         width?: number,         // optional column weight (default 1)
//       }],
//     }],
//     createdAt, updatedAt,
//   }
//
// Legacy canvases persisted a flat `items: [{ command, caption }]`; callers may
// still pass that shape to saveCanvasTemplate and it upgrades to one cell per
// row. See canvasLayout.js for the normalization every reader shares.
//
// Storage: localStorage, tenant-namespaced, parallel to kernelStore.
// Subscription pattern matches kernelStore exactly so CanvasesPage
// can listen for changes without polling.

import { getCurrentTenantIdSync } from "./userInfo.js";
import {
    BUILTIN_CANVASES,
    BUILTIN_CANVAS_RUN_CONCURRENCY,
    BUILTIN_CANVAS_SLUGS,
} from "./builtins/builtinCanvases.js";
import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";
import { normalizeRows, flattenCells } from "./canvasLayout.js";
import {
    executeCloudTenantViewCreate,
    getTenantViewCloudState,
    isStoredTenantViewBody,
    listCloudCanvases,
    loadTenantViewBody,
    loadTenantViewCloud,
    markTenantViewCloudUnavailable,
    prepareCloudTenantViewCreate,
    subscribeTenantViewCloud,
    tenantViewDefinitionDiffers,
    updateCloudTenantView,
} from "./tenantViewCloudStore.js";
import {
    findKernelInCatalogByCommand,
    findKernelInCatalogById,
} from "./kernelStore.js";
import { TenantViewUnavailableError } from "./tenantViewsApi.js";
import { openTenantViewWriteJournal } from "./tenantViewWriteJournal.js";
import {
    TenantViewLockUnavailableError,
    TenantViewOperationInProgressError,
    withTenantViewBrowserLock,
} from "./tenantViewBrowserLock.js";

const BUNDLED_CANVAS_DEFINITIONS = new WeakSet(BUILTIN_CANVASES);

const STORAGE_KEY_PREFIX = "originChat.canvasTemplates.v2";
const LEGACY_STORAGE_KEY_PREFIX = "originChat.canvasTemplates.v1";

// Resolve the bucket for a tenant. Like kernelStore, callers passed
// `null` historically (everything in a shared `:default` bucket), so
// we fall back to the signed-in tenant from /v1/me to keep saved
// canvases isolated per tenant. The `:default` bucket is used only
// before /v1/me resolves; its pre-scoping contents are preserved for
// cloud import.
function storageKey(tenantId) {
    discardLegacyTenantEntry(globalThis.localStorage, LEGACY_STORAGE_KEY_PREFIX, tenantId);
    return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

function resolvedBucketId(tenantId, currentTenantId = getCurrentTenantIdSync()) {
    return String((tenantId ?? currentTenantId) || "default");
}

function effectiveCloudTenantId(tenantId, cloudTenantId, currentTenantId) {
    const explicit = cloudTenantId === undefined ? null : cloudTenantId;
    const localTenant = tenantId && tenantId !== "default" ? tenantId : null;
    const value = explicit
        ?? localTenant
        ?? currentTenantId
        ?? getTenantViewCloudState(null).tenantId;
    return value ? String(value) : null;
}

function deferredCloudSave(staged, status, message) {
    return {
        ...staged,
        cloudSaveStatus: status,
        cloudSaveMessage: message,
    };
}

function committedWithLocalDurabilityWarning(saved) {
    return {
        ...saved,
        localDurabilityWarning:
            "The canvas was created in cloud storage, but its local recovery marker could not be saved. Retry to finish local cleanup.",
    };
}

function latestStagedCanvas(bucketId, staged) {
    return readRawUserCanvasTemplates(bucketId).find(
        (candidate) => candidate.id === staged.id,
    ) || staged;
}

function shouldRetireCreateRequest(error, { wasPending }) {
    if (error?.code === "INVALID_RESPONSE") return false;
    if (error?.code === "CLIENT_VALIDATION") return !wasPending;
    if (
        error?.serverMessage === "the idempotent create result was deleted"
        || error?.code === "ALREADY_EXISTS"
        || [400, 409, 413, 422].includes(Number(error?.status))
    ) {
        return true;
    }
    return false;
}

// Lossless migration read. Unlike listCanvasTemplates/readUserCanvasTemplates,
// this does not merge bundled canvases, filter colliding slugs, normalize the
// legacy `items` shape, or discard malformed rows. The bucket id is explicit so
// `default` remains addressable after the active tenant resolves.
//
// Absence, invalid JSON/shape, and unavailable storage are distinct outcomes:
// only absence is safely equivalent to an empty legacy bucket.
export function readRawCanvasTemplateBucket(bucketId = "default") {
    const id = bucketId == null || bucketId === "" ? "default" : String(bucketId);
    const key = storageKey(id);
    if (!key) return { status: "absent", key: null, records: [] };
    let raw;
    try {
        raw = localStorage.getItem(key);
    } catch (error) {
        return { status: "unavailable", reason: "storage", key, records: [], error };
    }
    if (raw === null) return { status: "absent", key, records: [] };

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (error) {
        return { status: "invalid", reason: "parse", key, records: [], error };
    }
    if (!Array.isArray(parsed)) {
        return {
            status: "invalid",
            reason: "shape",
            key,
            records: [],
            error: new TypeError("canvas template bucket must contain a JSON array"),
        };
    }
    return { status: "ok", key, records: parsed };
}

const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* listener errors don't block siblings */ }
    }
}

export function subscribeCanvasTemplates(fn) {
    listeners.add(fn);
    const unsubscribeCloud = subscribeTenantViewCloud(fn);
    return () => {
        listeners.delete(fn);
        unsubscribeCloud();
    };
}

// List canvases for a tenant: the read-only built-in set (shipped in code,
// never persisted) merged with the tenant's own saved canvases. Built-ins carry
// old timestamps so a user's recent work sorts above them. A user slug that
// collides with a built-in can't exist (saveCanvasTemplate rejects it), but we
// filter defensively so the immutable built-in always wins. (Tailored canvases
// from the profiling run are NOT merged here: they live in the scout chat's
// transcript and materialize as normal user canvases when clicked in the
// Tenant profile view.)
export function listCanvasTemplates(tenantId) {
    const cloud = listCloudCanvases(tenantId);
    const seen = new Set();
    return [
        ...BUILTIN_CANVASES,
        ...cloud.viewerOwned,
        ...readUserCanvasTemplates(tenantId),
        ...cloud.tenantVisible,
    ]
        .filter((canvas) => {
            if (!canvas?.slug || seen.has(canvas.slug)) return false;
            seen.add(canvas.slug);
            return true;
        })
        // Most-recently-updated first — list at the top of the admin page
        // should match the user's most recent work.
        .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

// Is this slug one of the shipped, read-only built-in canvases?
export function isBuiltinCanvas(slug) {
    return BUILTIN_CANVAS_SLUGS.has(sanitizeSlug(slug));
}

// Runtime authority is identity-based: a stored/server body cannot mint a
// bundled invocation merely by reusing a manifest slug or `builtin` field.
export function isBundledCanvasDefinition(canvas) {
    return Boolean(
        canvas
        && typeof canvas === "object"
        && BUNDLED_CANVAS_DEFINITIONS.has(canvas),
    );
}

export function bundledCanvasRunConcurrency(canvas) {
    if (!isBundledCanvasDefinition(canvas)) return undefined;
    return BUILTIN_CANVAS_RUN_CONCURRENCY[canvas.slug];
}

export function isStoredCanvasDefinition(canvas) {
    return isStoredTenantViewBody(canvas);
}

// Raw persisted (user-owned) canvases only — no built-ins merged. The write
// paths rebuild storage from THIS so built-ins are never baked into localStorage.
function readUserCanvasTemplates(tenantId) {
    try {
        const key = storageKey(tenantId);
        if (!key) return [];
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (t) => t && typeof t.id === "string" && typeof t.slug === "string"
                && !BUILTIN_CANVAS_SLUGS.has(t.slug),
        );
    } catch {
        return [];
    }
}

// Truly raw persisted canvases — valid shape only, NO built-in filtering. Used by
// the collision migration so it can see (and rename) a user row whose slug shadows
// a built-in, which readUserCanvasTemplates drops.
function readRawUserCanvasTemplates(tenantId) {
    try {
        const key = storageKey(tenantId);
        if (!key) return [];
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (t) => t && typeof t.id === "string" && typeof t.slug === "string",
        );
    } catch {
        return [];
    }
}

// Derive a slug from `root` that isn't taken (per `isTaken`), appending -2/-3/…
// on collision. Truncates the ROOT (not the appended suffix) to keep the result
// ≤ 64 chars, so the loop always terminates: slicing AFTER appending could chop
// `-2` back to `root` and spin forever once `root` is already ~64 chars.
function uniqueName(root, isTaken) {
    let candidate = root.slice(0, 64);
    let n = 1;
    while (isTaken(candidate)) {
        n += 1;
        const tag = `-${n}`;
        candidate = `${root.slice(0, 64 - tag.length)}${tag}`;
    }
    return candidate;
}

// One-time, idempotent upgrade migration — parallel to kernelStore's. A canvas a
// tenant saved before the built-in set shipped can carry a `slug` that now collides
// with a built-in (`shadow-ai` is the highest-risk: exactly what a security-minded
// user would name a hand-built governance canvas). readUserCanvasTemplates filters
// such a row out, and every write path rebuilds storage from that filtered reader,
// so the next save/delete would purge it irreversibly. Rename the colliding row to
// a free `<slug>-saved` instead of dropping it. Persists only when it renames.
export function migrateCollidingUserCanvasTemplates(tenantId) {
    const rows = readRawUserCanvasTemplates(tenantId);
    if (!rows.length) return false;
    const used = new Set(rows.map((t) => t.slug));
    let changed = false;
    for (const t of rows) {
        if (!BUILTIN_CANVAS_SLUGS.has(t.slug)) continue;
        const next = uniqueName(`${t.slug}-saved`, (s) => used.has(s) || BUILTIN_CANVAS_SLUGS.has(s));
        used.delete(t.slug);
        used.add(next);
        t.slug = next;
        t.updatedAt = Date.now();
        changed = true;
    }
    if (!changed) return false;
    const key = storageKey(tenantId);
    if (!key) return false;
    localStorage.setItem(key, JSON.stringify(rows));
    notify();
    return true;
}

export function findCanvasTemplateBySlug(tenantId, slug) {
    const target = sanitizeSlug(slug);
    if (!target) return null;
    return listCanvasTemplates(tenantId).find((t) => t.slug === target) || null;
}

export function findCanvasTemplateById(tenantId, id) {
    if (!id) return null;
    return listCanvasTemplates(tenantId).find((t) => t.id === id) || null;
}

// Reverse lookup: which saved canvases reference a given kernel
// command? Used by KernelInspector's "used in canvas: X, Y" line and
// by the delete-kernel confirm to warn about downstream impact.
export function findCanvasesUsingKernel(tenantId, command) {
    if (!command) return [];
    return listCanvasTemplates(tenantId).filter((t) =>
        flattenCells(t).some((cell) => cell.command === command),
    );
}

// Save (or overwrite) a canvas template. Matches the row it replaces by id
// when the caller carries one, so a slug edit moves the existing row instead
// of leaving a copy behind under the old slug. Returns the persisted template
// (with id + timestamps populated).
// Validate the slug a save targets. Split out of saveCanvasTemplate so the
// cloud update path applies exactly the same rules without touching localStorage.
function canvasSlugForSave(template) {
    if (!template || typeof template !== "object") {
        throw new Error("saveCanvasTemplate: template must be an object");
    }
    const slug = sanitizeSlug(template.slug);
    if (!slug) throw new Error("saveCanvasTemplate: invalid slug");
    // Built-in canvases are read-only and ship in code — never persist over one.
    // Fork it (forkCanvasTemplate) to get a mutable copy under a new slug.
    if (BUILTIN_CANVAS_SLUGS.has(slug)) {
        throw new Error(`"${slug}" is a built-in canvas (read-only). Fork it to customize.`);
    }
    return slug;
}

// Normalize a template into the record shape every storage path persists.
// `prior` is the record this save replaces: the localStorage row on the staging
// path, the loaded cloud body on the update path. It supplies the identity
// fields (id, createdAt) a save must carry forward rather than mint anew.
function normalizedCanvasTemplate(template, slug, prior = null) {
    // Accept either the current `rows` shape or a legacy flat `items` array;
    // normalizeRows upgrades the latter to one cell per row. Rows are the only
    // shape ever persisted from here on.
    //
    // One-way localStorage migration, no key bump: this keeps the same
    // `originChat.canvasTemplates.v1` key and stops writing `items`. Rolling
    // FORWARD is safe — every reader goes through normalizeRows, which still
    // reads any legacy `items` a prior build left behind. Rolling BACK to a
    // pre-rows build is not: it reads `t.items` with no `rows` fallback, so a
    // canvas saved/forked on this build renders empty until re-saved. That's
    // localStorage-only, per-user, and self-heals on the next save, so we
    // accept it rather than bump the key (which would orphan every existing
    // canvas) or carry a redundant `items` mirror.
    const rows = normalizeRows(template);
    // Empty canvases are valid at the storage layer (a freshly-created
    // canvas starts with zero rows; the user fills it in via the
    // editor). The edit form surfaces its own warning if a user tries
    // to save an empty canvas they were actively populating.
    return {
        id: prior?.id || template.id || generateId(),
        slug,
        title: typeof template.title === "string" && template.title.trim()
            ? template.title.trim()
            : slug,
        description: typeof template.description === "string" && template.description.trim()
            ? template.description.trim()
            : null,
        rows,
        createdAt: prior?.createdAt || Date.now(),
        updatedAt: Date.now(),
    };
}

// Save (or overwrite) a canvas template. Matches the row it replaces by id
// when the caller carries one, so a slug edit moves the existing row instead
// of leaving a copy behind under the old slug. Returns the persisted template
// (with id + timestamps populated).
export function saveCanvasTemplate(tenantId, template) {
    const persistenceKey = storageKey(tenantId);
    if (!persistenceKey) throw new Error("verified tenant and user identity are required");
    const slug = canvasSlugForSave(template);
    const existing = readUserCanvasTemplates(tenantId);
    const priorById = template.id
        ? existing.find((t) => t.id === template.id)
        : null;
    const occupant = existing.find((t) => t.slug === slug);
    // Moving a row onto a slug another canvas already holds would drop that
    // canvas, so refuse rather than overwrite. Saving in place is still fine.
    if (priorById && occupant && occupant !== priorById) {
        throw new Error(`"${slug}" is already used by another canvas.`);
    }
    const prior = priorById || occupant;
    const next = existing.filter((t) => t !== prior && t.slug !== slug);
    const finalTemplate = normalizedCanvasTemplate(template, slug, prior);
    next.push(finalTemplate);
    try {
        localStorage.setItem(persistenceKey, JSON.stringify(next));
    } catch (err) {
        throw new Error(`canvas template storage write failed: ${err?.message || err}`);
    }
    notify();
    return finalTemplate;
}

function runtimeCanvasDefinition(tenantId, template) {
    return {
        ...template,
        rows: normalizeRows(template).map((row) => ({
            ...row,
            cells: row.cells.map((cell) => {
                const kernel = cell.kernel_id
                    ? findKernelInCatalogById(tenantId, cell.kernel_id)
                    : findKernelInCatalogByCommand(tenantId, cell.command);
                const kernelId = kernel?.id;
                if (!kernelId) {
                    throw new Error(
                        `Kernel /${cell.command} is not synced yet. Reconnect and save the kernel before saving this canvas.`,
                    );
                }
                return {
                    ...cell,
                    kernel_id: kernelId,
                    command: kernel.command,
                };
            }),
        })),
    };
}

// The viewer-owned cloud canvas a save of `slug` would land on, if any.
//
// A canvas's slug IS its identity in cloud storage: tenant_views carries
// UNIQUE (tenant_id, created_by, kind, name) for tenant-owned rows, so a second
// CreateTenantView under a slug the viewer already owns can only ever come back
// 409 "name is already in use". Saving an existing slug is therefore an update
// of that row, never a create.
//
// Matched by slug alone. A rename (same row, new slug) deliberately does not
// route here - Studio sends those straight to updateCloudTenantView with the
// row's tenantViewId, which is the only caller that knows a rename is intended.
function viewerOwnedCloudCanvasBody(cloudTenantId, slug) {
    return listCloudCanvases(cloudTenantId)
        .viewerOwned
        .find((body) => body.slug === slug) || null;
}

// Does an unfinished create still own this canvas? A create whose response was
// lost, or whose completion marker could not be written, leaves a durable
// journal entry bound to one request id; replaying that exact request is what
// makes the create idempotent and retires both the entry and the staging row.
// The create machinery keeps priority so such a retry is never rerouted.
function hasOutstandingCanvasCreateJournal(cloudTenantId, bucketId, template, slug) {
    const priorLocal = readUserCanvasTemplates(bucketId).find(
        (candidate) => candidate.id === template.id || candidate.slug === slug,
    );
    const sourceKey = template.id || priorLocal?.id;
    if (!sourceKey) return false;
    try {
        return openTenantViewWriteJournal({
            tenantId: cloudTenantId,
            operation: "create",
            kind: "canvas",
            sourceKey,
        }).read() != null;
    } catch {
        // Malformed durable state fails closed through the create path, which
        // knows how to park it rather than write past it.
        return true;
    }
}

// Update the existing cloud row in place. Mirrors what Studio's canvas editor
// already does (CanvasesPage) so chat's canvas save agrees with it on what
// re-saving a slug means. No staging row: a local row shadowing a live cloud
// row would outrank it in the catalog.
async function updateExistingCloudCanvas(
    cloudTenantId,
    bucketId,
    body,
    template,
    slug,
    { lockManager },
) {
    const tenantViewId = body.tenantViewId;
    const lockIdentity = [
        encodeURIComponent(cloudTenantId),
        "canvas",
        encodeURIComponent(tenantViewId),
    ].join(":");
    try {
        // The merge in updateCloudTenantView diffs against the stored
        // definition, so a metadata-only catalog row has to be filled in first
        // or the edit would be merged onto an empty base.
        const base = body.bodyLoaded === true
            ? body
            : await loadTenantViewBody(tenantViewId, {
                tenantId: cloudTenantId,
                withDependencies: true,
            });
        // Resolves every cell's kernel to a cloud id, and throws the same
        // "not synced yet" error the create path raises when one is missing.
        const definition = runtimeCanvasDefinition(
            cloudTenantId,
            normalizedCanvasTemplate(template, slug, base),
        );
        const synchronized = await withTenantViewBrowserLock(
            lockIdentity,
            () => updateCloudTenantView(base, definition),
            { lockManager },
        );
        // A caller replaying an older staging row hands one back; exact-content
        // match leaves a genuinely newer local revision alone.
        deleteStagedCanvasRevision(bucketId, template);
        return synchronized;
    } catch (error) {
        if (error instanceof TenantViewOperationInProgressError) {
            return deferredCloudSave(template, "in_progress", error.message);
        }
        if (error instanceof TenantViewLockUnavailableError) {
            return deferredCloudSave(template, "cloud_unavailable", error.message);
        }
        if (error instanceof TenantViewUnavailableError) {
            markTenantViewCloudUnavailable(cloudTenantId, error);
            return deferredCloudSave(template, "cloud_unavailable", error.message);
        }
        throw error;
    }
}

// A create that came back ALREADY_EXISTS proves this tab's catalog is stale:
// another tab or device minted the slug after this tab went ready, and the
// pre-flight lookup only refreshes an idle or loading catalog. Without this,
// every retry repeats the create and 409s again until something else forces a
// reload. Refresh once and route to the row that now exists; return null when
// it still is not visible so the caller rethrows the original error.
async function recoverFromCanvasNameConflict(
    cloudTenantId,
    bucketId,
    template,
    slug,
    { lockManager },
) {
    const refreshed = await loadTenantViewCloud(cloudTenantId, { force: true });
    if (refreshed.status !== "ready") return null;
    const body = viewerOwnedCloudCanvasBody(cloudTenantId, slug);
    if (!body) return null;
    return updateExistingCloudCanvas(
        cloudTenantId,
        bucketId,
        body,
        template,
        slug,
        { lockManager },
    );
}

/**
 * Production authoring path. Local storage is retained only when the cloud is
 * unavailable; successful cloud creation removes the staging row.
 */
export async function saveCanvasTemplateCloudFirst(
    tenantId,
    template,
    { cloudTenantId, lockManager = globalThis.navigator?.locks } = {},
) {
    const currentTenantId = getCurrentTenantIdSync();
    const targetTenantId = effectiveCloudTenantId(
        tenantId,
        cloudTenantId,
        currentTenantId,
    );
    const bucketId = resolvedBucketId(tenantId, currentTenantId);
    if (targetTenantId) {
        let state = getTenantViewCloudState(targetTenantId);
        if (state.status === "idle" || state.status === "loading") {
            state = await loadTenantViewCloud(targetTenantId);
        }
        // Re-saving a slug the viewer already owns is an update, not a second
        // create. Resolved before staging so the update path never leaves a
        // local row shadowing the cloud row it just wrote.
        const slug = canvasSlugForSave(template);
        const existingBody =
            hasOutstandingCanvasCreateJournal(targetTenantId, bucketId, template, slug)
                ? null
                : viewerOwnedCloudCanvasBody(targetTenantId, slug);
        if (existingBody) {
            return updateExistingCloudCanvas(
                targetTenantId,
                bucketId,
                existingBody,
                template,
                slug,
                { lockManager },
            );
        }
    }
    const staged = saveCanvasTemplate(bucketId, template);
    if (!targetTenantId) {
        return deferredCloudSave(
            staged,
            "cloud_unavailable",
            "The active tenant was not resolved; this canvas remains stored locally.",
        );
    }
    const lockIdentity = [
        encodeURIComponent(targetTenantId),
        "canvas",
        encodeURIComponent(staged.id),
    ].join(":");
    try {
        return await withTenantViewBrowserLock(
            lockIdentity,
            () => saveStagedCanvasCloudFirst(bucketId, targetTenantId, staged, { lockManager }),
            { lockManager },
        );
    } catch (error) {
        if (error instanceof TenantViewOperationInProgressError) {
            return deferredCloudSave(
                latestStagedCanvas(bucketId, staged),
                "in_progress",
                error.message,
            );
        }
        if (error instanceof TenantViewLockUnavailableError) {
            return deferredCloudSave(staged, "cloud_unavailable", error.message);
        }
        throw error;
    }
}

async function saveStagedCanvasCloudFirst(bucketId, cloudTenantId, staged, { lockManager } = {}) {
    let state = getTenantViewCloudState(cloudTenantId);
    if (state.status === "idle" || state.status === "loading") {
        state = await loadTenantViewCloud(cloudTenantId);
    }
    const canRetryPendingCreate =
        state.status === "unavailable" && state.capabilities != null;
    if (state.status !== "ready" && !canRetryPendingCreate) {
        return deferredCloudSave(
            staged,
            "cloud_unavailable",
            state.error?.message || "Cloud view storage is unavailable; this canvas remains stored locally.",
        );
    }
    const journal = openTenantViewWriteJournal({
        tenantId: cloudTenantId,
        operation: "create",
        kind: "canvas",
        sourceKey: staged.id,
    });
    const durable = journal.read();
    if (durable?.state === "complete") {
        const refreshed = await loadTenantViewCloud(state.tenantId, { force: true });
        if (refreshed.status !== "ready") {
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                refreshed.error?.message || "Cloud view storage is unavailable; this canvas remains stored locally.",
            );
        }
        const row = refreshed.rows.find(
            (candidate) => candidate.metadata.id === durable.tenantViewId,
        );
        if (!row) {
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                "The previously created canvas is not in the readable catalog.",
            );
        }
        const resumed = await loadTenantViewBody(durable.tenantViewId, {
            tenantId: cloudTenantId,
            withDependencies: true,
        });
        let synchronized = resumed;
        const definition = runtimeCanvasDefinition(cloudTenantId, staged);
        if (tenantViewDefinitionDiffers(resumed, definition)) {
            synchronized = await updateCloudTenantView(resumed, definition);
            try {
                journal.complete(
                    durable,
                    synchronized.tenantViewId,
                    synchronized.version,
                );
            } catch {
                return committedWithLocalDurabilityWarning(synchronized);
            }
        }
        if (!deleteStagedCanvasRevision(bucketId, staged)) {
            return deferredCloudSave(
                latestStagedCanvas(bucketId, staged),
                "newer_revision",
                "A newer local canvas revision is waiting to be saved.",
            );
        }
        journal.clear();
        return synchronized;
    }
    if (durable?.state === "pending" && !durable.request) {
        return deferredCloudSave(
            staged,
            "legacy_journal_parked",
            "This interrupted canvas save predates durable request binding and was parked safely.",
        );
    }
    const wasPending = durable?.state === "pending";
    const pending = durable || journal.begin(
        prepareCloudTenantViewCreate(
            "canvas",
            runtimeCanvasDefinition(cloudTenantId, staged),
            { capabilities: state.capabilities },
        ),
    );

    let saved;
    try {
        saved = await executeCloudTenantViewCreate(pending.request, {
            capabilities: state.capabilities,
            createRequestId: pending.createRequestId,
        });
    } catch (error) {
        if (error instanceof TenantViewUnavailableError) {
            markTenantViewCloudUnavailable(state.tenantId, error);
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                error.message,
            );
        }
        if (shouldRetireCreateRequest(error, { wasPending })) journal.clear();
        if (error?.code === "ALREADY_EXISTS") {
            const recovered = await recoverFromCanvasNameConflict(
                cloudTenantId,
                bucketId,
                staged,
                staged.slug,
                { lockManager },
            );
            if (recovered) return recovered;
        }
        throw error;
    }
    try {
        journal.complete(pending, saved.tenantViewId, saved.version);
    } catch {
        return committedWithLocalDurabilityWarning(saved);
    }
    let synchronized = saved;
    const definition = runtimeCanvasDefinition(cloudTenantId, staged);
    const currentRequest = prepareCloudTenantViewCreate(
        "canvas",
        definition,
        { capabilities: state.capabilities },
    );
    if (
        wasPending
        && JSON.stringify(currentRequest) !== JSON.stringify(pending.request)
    ) {
        synchronized = await updateCloudTenantView(saved, definition);
        try {
            journal.complete(
                pending,
                synchronized.tenantViewId,
                synchronized.version,
            );
        } catch {
            return committedWithLocalDurabilityWarning(synchronized);
        }
    }
    if (!deleteStagedCanvasRevision(bucketId, staged)) {
        return deferredCloudSave(
            latestStagedCanvas(bucketId, staged),
            "newer_revision",
            "A newer local canvas revision is waiting to be saved.",
        );
    }
    journal.clear();
    return synchronized;
}

function deleteStagedCanvasRevision(bucketId, staged) {
    const existing = readRawUserCanvasTemplates(bucketId);
    const next = existing.filter((candidate) => {
        if (candidate.id !== staged.id) return true;
        // Re-saving an identical canvas while its create is in flight advances
        // only updatedAt. Treat it as the same revision so the successful cloud
        // result can clear staging instead of fabricating `newer_revision`.
        const { updatedAt: _candidateUpdatedAt, ...candidateRevision } = candidate;
        const { updatedAt: _stagedUpdatedAt, ...stagedRevision } = staged;
        return JSON.stringify(candidateRevision) !== JSON.stringify(stagedRevision);
    });
    if (next.length === existing.length) return false;
    const key = storageKey(bucketId);
    if (!key) return false;
    localStorage.setItem(key, JSON.stringify(next));
    notify();
    return true;
}

// Create a new blank canvas template with a unique slug. Used by the
// Library sidebar's "+ new" affordance. Starts empty — the user
// populates it via the canvas detail editor.
export function createBlankCanvasTemplate(tenantId) {
    const existing = listCanvasTemplates(tenantId);
    const used = new Set(existing.map((t) => t.slug));
    let n = 1;
    let slug = "new-canvas-1";
    while (used.has(slug)) {
        n += 1;
        slug = `new-canvas-${n}`;
    }
    return saveCanvasTemplate(tenantId, {
        slug,
        title: "Untitled canvas",
        description: null,
        rows: [],
    });
}

export async function createBlankCanvasTemplateCloudFirst(tenantId) {
    const existing = listCanvasTemplates(tenantId);
    const used = new Set(existing.map((t) => t.slug));
    let n = 1;
    let slug = "new-canvas-1";
    while (used.has(slug)) {
        n += 1;
        slug = `new-canvas-${n}`;
    }
    return saveCanvasTemplateCloudFirst(tenantId, {
        slug,
        title: "Untitled canvas",
        description: null,
        rows: [],
    });
}

export function deleteCanvasTemplate(tenantId, slug) {
    const target = sanitizeSlug(slug);
    if (!target) return false;
    // Built-ins ship in code and have no storage row to remove.
    if (BUILTIN_CANVAS_SLUGS.has(target)) return false;
    const existing = readUserCanvasTemplates(tenantId);
    const next = existing.filter((t) => t.slug !== target);
    if (next.length === existing.length) return false;
    const key = storageKey(tenantId);
    if (!key) return false;
    localStorage.setItem(key, JSON.stringify(next));
    notify();
    return true;
}

// Fork a canvas (built-in or user) into a fresh, mutable user-owned copy under a
// unique slug. The way to "customize" a read-only built-in: the original is
// untouched; the copy is a normal saved canvas the user can edit and delete.
// Returns the persisted copy, or null if the source slug isn't found.
export function forkCanvasTemplate(tenantId, slug) {
    const source = findCanvasTemplateBySlug(tenantId, slug);
    if (!source) return null;
    const used = new Set(listCanvasTemplates(tenantId).map((t) => t.slug));
    const next = uniqueName(`${sanitizeSlug(slug)}-copy`, (s) => used.has(s));
    return saveCanvasTemplate(tenantId, {
        slug: next,
        title: source.title ? `${source.title} (copy)` : next,
        description: source.description || null,
        // normalizeRows returns fresh objects, so the fork shares no structure
        // with the source (built-in or user) — also upgrades a legacy source.
        rows: normalizeRows(source),
    });
}

export async function forkCanvasTemplateCloudFirst(tenantId, slug) {
    const source = findCanvasTemplateBySlug(tenantId, slug);
    if (!source) return null;
    const used = new Set(listCanvasTemplates(tenantId).map((t) => t.slug));
    const next = uniqueName(`${sanitizeSlug(slug)}-copy`, (s) => used.has(s));
    return saveCanvasTemplateCloudFirst(tenantId, {
        slug: next,
        title: source.title ? `${source.title} (copy)` : next,
        description: source.description || null,
        rows: normalizeRows(source),
    });
}

export function sanitizeSlug(raw) {
    if (!raw) return "";
    return String(raw)
        .trim()
        .toLowerCase()
        .replace(/^\/+/, "")
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

function generateId() {
    return `ct_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
